import { useId, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { scoreBand } from "./qualityApi";
import type { FieldFinding, ScoreBand } from "./qualityApi";
import type { FieldQuality } from "./useFieldQuality";

const NOTE_STYLE = {
    marginTop: 7,
    display: "flex",
    alignItems: "flex-start",
    gap: 9,
    fontSize: 11.5,
    lineHeight: 1.5
} as const;

function FindingList({ findings, alignment = false }: { findings?: FieldFinding[]; alignment?: boolean }) {
    if (!findings?.length) return null;
    return (
        <div style={{ display: "grid", gap: 4, marginTop: 5 }}>
            {findings.map(finding => (
                <div key={finding.code} style={{ color: "var(--fg3)" }}>
                    <strong style={{ color: "var(--fg2)", fontWeight: 700 }}>{finding.criterion}</strong>
                    <span style={{ color: "var(--fg4)" }}> · {finding.status === "present" ? "Present" : finding.status === "partial" ? "Partial" : "Missing"}</span>
                    {alignment && finding.explanation && <span> · {finding.explanation}</span>}
                    {finding.suggestion && <span> · {finding.suggestion}</span>}
                </div>
            ))}
        </div>
    );
}

function FindingDisclosure({ label, findings, alignment = false, open, onToggle, panelId }: {
    label: string;
    findings?: FieldFinding[];
    alignment?: boolean;
    open: boolean;
    onToggle: () => void;
    panelId: string;
}) {
    if (!findings?.length) return null;
    return (
        <div style={{ minWidth: 0, marginTop: 7 }}>
            <button
                id={`${panelId}-trigger`}
                type="button"
                aria-expanded={open}
                aria-controls={panelId}
                onClick={onToggle}
                style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 9,
                    padding: "3px 0",
                    border: 0,
                    background: "transparent",
                    color: "var(--fg3)",
                    cursor: "pointer",
                    textAlign: "left",
                    fontFamily: "inherit",
                    fontSize: "inherit",
                    fontWeight: 600
                }}
            >
                <span>{label}</span>
                <span aria-hidden style={{
                    marginLeft: "auto",
                    color: "var(--fg4)",
                    fontSize: 17,
                    lineHeight: 1,
                    transform: open ? "rotate(180deg)" : "none"
                }}>▾</span>
            </button>
            <div id={panelId} hidden={!open} aria-labelledby={`${panelId}-trigger`} style={{ overflowWrap: "anywhere" }}>
                <FindingList findings={findings} alignment={alignment} />
            </div>
        </div>
    );
}

function alignmentSummary(findings: FieldFinding[], score: number): ScoreBand {
    // Historical responses may not have findings. Their numeric band remains
    // the only available summary; avoid inventing a relationship judgment.
    if (findings.length === 0) return scoreBand(score);
    if (findings.some(finding => finding.relationshipOutcome === "unrelated")) {
        return { label: "Alignment mismatch", color: "var(--error, #b42318)" };
    }
    if (findings.some(finding => finding.relationshipOutcome === "insufficient_information" || finding.status === "missing")) {
        return { label: "Needs clarification", color: "var(--np-warn)" };
    }
    if (findings.some(finding => finding.relationshipOutcome === "partial" || finding.status === "partial")) {
        return { label: "Partially aligned", color: "var(--np-warn)" };
    }
    return { label: "Aligned", color: "var(--np-success)" };
}

function ScoreSummary({ score, feedback, summary }: { score: number; feedback: string; summary?: ScoreBand }) {
    const band = summary ?? scoreBand(score);
    return (
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: "4px 9px", minWidth: 0 }}>
            <span
                style={{
                    flexShrink: 0,
                    padding: "2px 7px",
                    borderRadius: 999,
                    border: `1px solid ${band.color}`,
                    color: band.color,
                    fontWeight: 700,
                    whiteSpace: "nowrap"
                }}
            >
                {band.label}
            </span>
            <span style={{ flex: "1 1 220px", minWidth: 0, overflowWrap: "anywhere", color: "var(--fg3)", marginTop: 2 }}>{feedback}</span>
        </div>
    );
}

/** Verbal evaluation summaries and optional details, with numeric scores kept internal. */
export function FieldQualityNote({ label, quality, disclosureScope }: {
    label: string;
    quality: FieldQuality;
    /** A screen/draft identity, independent of autosave or evaluation revisions. */
    disclosureScope?: string;
}) {
    return <FieldQualityContent key={JSON.stringify([disclosureScope ?? "field", label])} label={label} quality={quality} />;
}

function FieldQualityContent({ label, quality }: { label: string; quality: FieldQuality }) {
    // Keep user choices through loading, failures, and feedback updates. A
    // different screen/draft scope remounts this content with closed details.
    const [qualityOpen, setQualityOpen] = useState(false);
    const [alignmentOpen, setAlignmentOpen] = useState(false);
    const qualityPanelId = useId();
    const alignmentPanelId = useId();
    if (quality.status === "idle") return null;

    if (quality.status === "scoring") {
        return (
            <div style={NOTE_STYLE} role="status" aria-live="polite" aria-label={`${label} evaluation`}>
                <CircularProgress size={11} sx={{ color: "var(--np-brand)", marginTop: "2px" }} />
                <span style={{ color: "var(--fg3)", fontWeight: 600 }}>Evaluating…</span>
            </div>
        );
    }

    if (quality.status === "error") {
        return (
            <div style={NOTE_STYLE} role="status" aria-live="polite" aria-label={`${label} evaluation`}>
                <span style={{ color: "var(--fg4)" }}>{quality.message}</span>
            </div>
        );
    }

    if (quality.alignment || quality.findings?.length) {
        return (
            <div style={{ ...NOTE_STYLE, display: "grid", gap: 8 }} role="status" aria-live="polite" aria-label={`${label} evaluation`}>
                {quality.notice && (
                    <div
                        style={{
                            padding: "7px 9px",
                            borderRadius: 7,
                            background: quality.notice.kind === "update_failed" ? "#fff8eb" : "#f5f7fc",
                            color: "var(--fg3)",
                            fontWeight: 600
                        }}
                    >
                        {quality.notice.message}
                    </div>
                )}
                <div>
                    <div style={{ marginBottom: 5, color: "var(--fg4)", fontSize: 9.5, fontWeight: 750, letterSpacing: ".06em" }}>FIELD QUALITY</div>
                    <ScoreSummary score={quality.score} feedback={quality.feedback} />
                    <FindingDisclosure label="Field quality details" findings={quality.findings}
                        open={qualityOpen} onToggle={() => setQualityOpen(open => !open)} panelId={qualityPanelId} />
                </div>
                {quality.alignment && (
                    <div style={{ paddingTop: 7, borderTop: "1px solid var(--divider)" }}>
                        <div style={{ marginBottom: 5, color: "var(--fg4)", fontSize: 9.5, fontWeight: 750, letterSpacing: ".06em" }}>ALIGNMENT</div>
                        {quality.alignment.status === "ready" && quality.alignment.score !== null ? (
                            <ScoreSummary
                                score={quality.alignment.score}
                                feedback={quality.alignment.feedback}
                                summary={alignmentSummary(quality.alignment.findings, quality.alignment.score)}
                            />
                        ) : (
                            <div style={{ display: "flex", alignItems: "flex-start", gap: 9 }}>
                                <span style={{ flexShrink: 0, padding: "2px 7px", borderRadius: 999, background: "var(--bg-input)", color: "var(--fg4)", fontWeight: 700 }}>
                                    {quality.notice?.kind === "updating"
                                        ? "Updating"
                                        : quality.alignment.status === "unavailable" ? "Unavailable" : "Not applicable"}
                                </span>
                                <span style={{ color: "var(--fg3)", marginTop: 2 }}>{quality.alignment.feedback}</span>
                            </div>
                        )}
                        <FindingDisclosure label="Alignment details" findings={quality.alignment.findings} alignment
                            open={alignmentOpen} onToggle={() => setAlignmentOpen(open => !open)} panelId={alignmentPanelId} />
                    </div>
                )}
            </div>
        );
    }

    return (
        <div style={{ ...NOTE_STYLE, display: "grid", gap: 7 }} role="status" aria-live="polite" aria-label={`${label} evaluation`}>
            {quality.notice && <span style={{ color: "var(--fg3)", fontWeight: 600 }}>{quality.notice.message}</span>}
            <ScoreSummary score={quality.score} feedback={quality.feedback} />
        </div>
    );
}
